import { useEffect, useMemo, useRef, useState } from "react";
import { groupJobsForPicker } from "../jobPicker";

// Searchable job picker used in Expense Management. Replaces the long native job dropdown.
// Props:
//   jobs         from toPickerJobs (src/jobPicker.js)
//   range        { from, to } day numbers for the expense date(s), or null (then no "running" group)
//   value        chosen job id, or ""
//   onChange     called with the job id ("" when the choice is cleared)
//   placeholder  text shown while nothing is chosen, e.g. "Assign to job..."
//   clearLabel   optional text for an item at the top that clears the choice
// Finished jobs stay collapsed until the owner opens them or types in the search box.
export function JobPicker({ jobs, range, value, onChange, placeholder, clearLabel, ariaLabel, style }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showFinished, setShowFinished] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const idBase = useRef("jp" + Math.random().toString(36).slice(2, 8)).current;

  const rangeFrom = range ? range.from : null;
  const rangeTo = range ? range.to : null;
  // Grouping only happens while the panel is open, and depends on the date values, not the range object.
  const groups = useMemo(
    () => (open ? groupJobsForPicker(jobs, rangeFrom === null ? null : { from: rangeFrom, to: rangeTo }, query) : []),
    [open, jobs, rangeFrom, rangeTo, query]);
  const searching = query.trim() !== "";
  const finishedOpen = searching || showFinished;
  const finishedGroup = groups.find(g => g.key === "finished");

  // Flat list of the choices that can be picked right now (drives the arrow keys)
  const flat = useMemo(() => {
    const out = [];
    if (clearLabel && value && !searching) out.push({ value: "", label: clearLabel, client: "", clear: true });
    groups.forEach(g => { if (g.key !== "finished" || finishedOpen) g.jobs.forEach(j => out.push(j)); });
    return out;
  }, [groups, finishedOpen, clearLabel, value, searching]);

  const chosen = (jobs || []).find(j => j.value === value);

  function close(refocus) {
    setOpen(false); setQuery(""); setShowFinished(false); setActive(0);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }
  function choose(item) {
    onChange(item.value);
    close(true);
  }

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target)) close(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function onKeyDown(e) {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); return; }
    // Arrows and Enter belong to the search box; Enter on the finished-jobs toggle must only toggle it.
    if (e.target !== e.currentTarget.querySelector(".jp-search")) return;
    if (e.key === "ArrowDown") { e.preventDefault(); if (flat.length) setActive(a => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[active]) choose(flat[active]); }
  }

  const font = "'DM Sans',sans-serif";
  let idx = -1;
  const renderOption = (j) => {
    idx += 1;
    const i = idx;
    return (
      <div key={(j.clear ? "clear" : j.value)} id={`${idBase}-o${i}`} data-idx={i} role="option" aria-selected={j.value === value}
        className={`jp-option${i === active ? " on" : ""}`}
        onMouseDown={e => e.preventDefault()} onClick={() => choose(j)} onMouseEnter={() => setActive(i)}>
        <span className="jp-name">{j.label}</span>
        {j.client ? <span className="jp-client">{j.client}</span> : null}
      </div>
    );
  };

  return (
    <div ref={rootRef} className="jp-root" style={style}
      onBlur={e => { if (open && e.relatedTarget && rootRef.current && !rootRef.current.contains(e.relatedTarget)) close(false); }}>
      <button type="button" ref={buttonRef} className="job-select jp-button" aria-haspopup="listbox" aria-expanded={open}
        aria-label={ariaLabel || placeholder} onClick={() => (open ? close(false) : setOpen(true))}>
        <span style={{ color: chosen ? undefined : "inherit", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", flex: 1 }}>
          {chosen ? chosen.label : placeholder}
        </span>
        <span aria-hidden="true" style={{ marginLeft: 8, fontSize: 10 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="jp-panel" onKeyDown={onKeyDown} onMouseDown={e => { if (e.target.tagName !== "INPUT") e.preventDefault(); }}>
          <input autoFocus type="text" className="jp-search" placeholder="Search by job or client" value={query}
            onChange={e => setQuery(e.target.value)} role="combobox" aria-expanded="true" aria-controls={`${idBase}-list`}
            aria-activedescendant={flat.length ? `${idBase}-o${active}` : undefined} aria-label="Search jobs" />
          <div ref={listRef} id={`${idBase}-list`} role="listbox" className="jp-list">
            {flat[0] && flat[0].clear && renderOption(flat[0])}
            {groups.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12, color: "inherit", fontFamily: font }}>No jobs match.</div>}
            {groups.filter(g => g.key !== "finished" || finishedOpen).map(g => (
              <div key={g.key} role="group" aria-label={g.label}>
                <div className="jp-group">{g.label}</div>
                {g.jobs.map(renderOption)}
              </div>
            ))}
          </div>
          {finishedGroup && !searching && (
            <button type="button" className="jp-toggle" onMouseDown={e => e.preventDefault()} onClick={() => setShowFinished(v => !v)}>
              {showFinished ? "Hide finished jobs" : `Show finished jobs (${finishedGroup.jobs.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
